import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useAppDispatch, useTrading } from "features/store";
import {
  useListAccountStrategiesQuery,
  useGetRemovedStrategiesQuery,
  Strategy,
} from "features/trading/accounts";
import {
  SelectedStrategyKind,
  setSelectedStrategy,
} from "features/trading/tradingSlice";
import { useEffect } from "react";

const optionValue = (kind: SelectedStrategyKind, id: number) => `${kind}-${id}`;

export const AccountStrategyPicker = () => {
  const dispatch = useAppDispatch();
  const { currentAccountId, selectedStrategy } = useTrading();

  useEffect(() => {
    dispatch(setSelectedStrategy(undefined));
  }, [currentAccountId, dispatch]);

  const { data, isLoading } = useListAccountStrategiesQuery(
    { filters: { account_id: currentAccountId } },
    { skip: !currentAccountId },
  );

  const { data: removedData, isLoading: isRemovedLoading } =
    useGetRemovedStrategiesQuery(currentAccountId, {
      skip: !currentAccountId,
    });

  if (isLoading || !data || isRemovedLoading) {
    return <CircularProgress />;
  }

  const strategies = data.results;
  const removedStrategies: Strategy[] = removedData?.results || [];
  const hasRemoved = removedStrategies.length > 0;

  const selectValue = selectedStrategy
    ? optionValue(selectedStrategy.kind, selectedStrategy.data.id)
    : "";

  return (
    <Box display="flex" alignItems="center">
      <Select
        labelId="account-strategy-label"
        label="Strategy"
        size="small"
        value={selectValue}
        sx={{ width: 250, borderRadius: 5 }}
        onChange={(event) => {
          const raw = event.target.value as string;
          const [kind, idRaw] = raw.split("-");
          const id = Number(idRaw);

          if (kind === SelectedStrategyKind.ACTIVE) {
            const active = strategies.find((s) => s.id === id);
            if (active) {
              dispatch(
                setSelectedStrategy({
                  kind: SelectedStrategyKind.ACTIVE,
                  data: active,
                }),
              );
            }
            return;
          }

          if (kind === SelectedStrategyKind.REMOVED) {
            const removed = removedStrategies.find((s) => s.id === id);
            if (removed && currentAccountId !== undefined) {
              dispatch(
                setSelectedStrategy({
                  kind: SelectedStrategyKind.REMOVED,
                  data: removed,
                  accountId: currentAccountId,
                }),
              );
            }
          }
        }}
        disabled={strategies.length === 0 && !hasRemoved}
      >
        {strategies.map((strat) => (
          <MenuItem
            key={`${SelectedStrategyKind.ACTIVE}-${strat.id}`}
            value={optionValue(SelectedStrategyKind.ACTIVE, strat.id)}
          >
            {strat.strategy__name}
          </MenuItem>
        ))}
        {hasRemoved &&
          removedStrategies.map((strat) => (
            <MenuItem
              key={`${SelectedStrategyKind.REMOVED}-${strat.id}`}
              value={optionValue(SelectedStrategyKind.REMOVED, strat.id)}
            >
              {strat.name}{" "}
              <Typography component="span" variant="caption" color="grey.500">
                (Removed)
              </Typography>
            </MenuItem>
          ))}
      </Select>
    </Box>
  );
};
